import { WorkoutExerciseType } from '../enums';
import { z } from 'zod';

import { CoverSourceName, MediaSourceValue } from './asset-source';
import { Translation } from './translation';

// The stringified accidents of a broken value pipeline, never a
// prescription: the old FE turned a RANGE value into `Number('80-130')` →
// 'NaN' on every load, and the save then overwrote the stored value with it
// while wiping every client's in-flight session. A stale bundle still runs
// that code during a deploy window; refusing the spelling at the edge is the
// only way the server can protect a value it cannot recover.
const CORRUPTED_VALUE_SPELLINGS = new Set(['NaN', 'null', 'undefined']);

const workoutExerciseRound = z.object({
  configurables: z.array(
    z.object({
      isChecked: z.boolean().optional(),
      configurableId: z.string(),
      configurableValue: z
        .string()
        .optional()
        .or(z.literal(''))
        .refine(
          (value) =>
            value === undefined || !CORRUPTED_VALUE_SPELLINGS.has(value),
          'VALIDATION__InvalidConfigurableValue',
        ),
    }),
  ),
});

// An alternate stands in for its primary exercise, so it can't have alternates
// of its own (no chaining). `alternates` is kept here (instead of omitted) so a
// chained payload survives parsing instead of being silently stripped —
// bulkCreateWorkoutExercises rejects it explicitly with a 400.
const workoutExerciseAlternate = z.object({
  id: z.string(),
  comment: z.string().optional().nullable(),
  rounds: z.array(workoutExerciseRound),
  alternates: z.array(z.unknown()).optional(),
});

const workoutExercise = z.object({
  id: z.string(),
  comment: z.string().optional().nullable(),
  rounds: z.array(workoutExerciseRound),
  alternates: z.array(workoutExerciseAlternate).optional(),
});

// WARMUP and COOLDOWN keep their fixed label and can never be renamed — the
// refine enforces that server-side rather than trusting the FE to withhold it.
const workoutGroup = z
  .object({
    type: z.enum([
      WorkoutExerciseType.COOLDOWN,
      WorkoutExerciseType.MAIN,
      WorkoutExerciseType.WARMUP,
    ]),
    name: z.string().trim().max(255, 'VALIDATION__Max255CharLimit').nullish(),
    exercises: z.array(workoutExercise),
  })
  .superRefine((group, ctx) => {
    if (group.name && group.type !== WorkoutExerciseType.MAIN) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'VALIDATION__GroupNameNotAllowed',
        path: ['name'],
      });
    }
  });

export const CreateWorkoutDto = z.object({
  mediaType: z.string().optional(),
  mediaSource: MediaSourceValue.optional(),
  coverSource: CoverSourceName,
  coverType: z.string(),
  organizationId: z.string().uuid().nullish(),
  level: z
    .number()
    .min(0, 'VALIDATION__NonNegative')
    .max(5, 'VALIDATION__MaxValue5'),
  tags: z.array(z.string()),
  translations: z.array(Translation),
  restTimes: z.object({
    afterExercise: z.number().nonnegative('VALIDATION__NonNegative'),
    afterRound: z.number().nonnegative('VALIDATION__NonNegative'),
    afterGroup: z.number().nonnegative('VALIDATION__NonNegative'),
  }),
  groups: z.array(workoutGroup),
  configuration: z
    .object({
      organizationIds: z.array(z.string()).optional(),
      marketplace: z
        .object({
          enabled: z.boolean(),
          price: z.number().nonnegative().optional(),
          currency: z.string().optional(),
        })
        .optional(),
      isOrg: z.boolean().optional(),
    })
    .optional(),
});
export type ApiCreateWorkoutDto = z.infer<typeof CreateWorkoutDto>;

export const UpdateWorkoutDto = z.object({
  mediaType: z.string().optional(),
  mediaSource: MediaSourceValue.optional(),
  coverSource: CoverSourceName.optional().nullable(),
  coverType: z.string().optional().nullable(),
  level: z
    .number()
    .min(0, 'VALIDATION__NonNegative')
    .max(5, 'VALIDATION__MaxValue5'),
  tags: z.array(z.string()).optional(),
  translations: z.array(Translation).optional(),
  restTimes: z
    .object({
      afterExercise: z.number().nonnegative('VALIDATION__NonNegative'),
      afterRound: z.number().nonnegative('VALIDATION__NonNegative'),
      afterGroup: z.number().nonnegative('VALIDATION__NonNegative'),
    })
    .optional(),
  groups: z.array(workoutGroup).optional(),
  configuration: z
    .object({
      organizationIds: z.array(z.string()).optional(),
      marketplace: z
        .object({
          enabled: z.boolean(),
          price: z.number().nonnegative().optional(),
          currency: z.string().optional(),
        })
        .optional(),
      isOrg: z.boolean().optional(),
    })
    .optional(),
});

export type ApiUpdateWorkoutDto = z.infer<typeof UpdateWorkoutDto>;

export const ValidationWorkoutDto = z.object({
  mediaType: z.string().optional(),
  mediaSource: MediaSourceValue.optional(),
  coverSource: CoverSourceName.optional(),
  coverType: z.string().optional(),
  level: z
    .number()
    .min(0, 'VALIDATION__NonNegative')
    .max(5, 'VALIDATION__MaxValue5')
    .optional(),
  tags: z.array(z.string()).optional(),
  translations: z.array(Translation).optional(),
  restTimes: z
    .object({
      afterExercise: z.number().nonnegative('VALIDATION__NonNegative'),
      afterRound: z.number().nonnegative('VALIDATION__NonNegative'),
      afterGroup: z.number().nonnegative('VALIDATION__NonNegative'),
    })
    .optional(),
  groups: z
    .array(
      z.object({
        type: z.enum([
          WorkoutExerciseType.COOLDOWN,
          WorkoutExerciseType.MAIN,
          WorkoutExerciseType.WARMUP,
        ]),
        // THE SHARED ROUND, not a fourth hand-rolled copy of it.
        //
        // This route is the PRE-FLIGHT of the create: the frontend asks it
        // whether a payload will be accepted before sending it for real, so an
        // answer that disagrees with `CreateWorkoutDto` is worse than no answer
        // at all. The inline copy that stood here never received the
        // `CORRUPTED_VALUE_SPELLINGS` refine, so `/validate` said 200 to exactly
        // the stale-bundle payload `POST /v2/workouts` then refused with 422 —
        // the one case the refine was added for.
        exercises: z.array(
          z.object({
            id: z.string(),
            rounds: z.array(workoutExerciseRound),
          }),
        ),
      }),
    )
    .optional(),
  configuration: z
    .object({
      marketplace: z
        .object({
          enabled: z.boolean(),
          price: z.number().nonnegative().optional(),
          currency: z.string().optional(),
        })
        .optional(),
      isOrg: z.boolean().optional(),
    })
    .optional(),
});

export type ApiValidationWorkoutDto = z.infer<typeof ValidationWorkoutDto>;
